import { useMemo } from 'react';
import styled from 'styled-components';
import { GetMenuProductArrangementsQuery } from 'data/__generated__';
import { ProductArrangementsToUpdate, SupplierSummary } from './types';
import { SupplierCard } from './SupplierCard';

export const MenuProductSummary = ({
    menuProductArrangements,
    productArrangementsToUpdate,
}: {
    menuProductArrangements: GetMenuProductArrangementsQuery['menuProductArrangements'];
    productArrangementsToUpdate: ProductArrangementsToUpdate;
}) => {
    const supplierSummaries = useMemo(() => {
        const productQuantities = new Map<
            string,
            {
                productId: string;
                productName: string;
                supplierId: string;
                supplierName: string;
                quantity: number;
                imageUrl?: string | null;
            }
        >();

        // Iterate through all menu product arrangements
        menuProductArrangements.forEach(({ productArrangementId, productArrangementIndexes }) => {
            productArrangementIndexes.forEach(({ productArrangementIndex, rotations }) => {
                rotations.forEach(({ rotationNumber, products }) => {
                    products.forEach(({ product, quantity }) => {
                        // Check if there's a replacement product
                        const replacement =
                            productArrangementsToUpdate?.[productArrangementId]?.[productArrangementIndex]?.[
                                rotationNumber
                            ];

                        // Determine which product to use (replacement or original)
                        const effectiveProduct = replacement?.newProduct || product;

                        // Skip if product is null (removed)
                        if (!effectiveProduct) return;

                        const supplierId = effectiveProduct.supplier?._id || 'no-supplier';
                        const supplierName = effectiveProduct.supplier?.name || 'Sans fournisseur';
                        const productId = effectiveProduct._id;

                        const key = `${supplierId}-${productId}`;

                        if (productQuantities.has(key)) {
                            const existing = productQuantities.get(key)!;
                            existing.quantity += quantity;
                        } else {
                            productQuantities.set(key, {
                                productId,
                                productName: effectiveProduct.fullname,
                                supplierId,
                                supplierName,
                                quantity,
                                imageUrl: effectiveProduct.imageUrl,
                            });
                        }
                    });
                });
            });
        });

        // Group by supplier
        const supplierMap = new Map<string, SupplierSummary>();

        productQuantities.forEach((productData) => {
            const { supplierId, supplierName, productId, productName, quantity, imageUrl } = productData;

            if (!supplierMap.has(supplierId)) {
                supplierMap.set(supplierId, {
                    supplierId,
                    supplierName,
                    products: [],
                });
            }

            supplierMap.get(supplierId)!.products.push({
                productId,
                productName,
                totalQuantity: quantity,
                imageUrl,
            });
        });

        // Convert to array and sort
        return Array.from(supplierMap.values())
            .sort((a, b) => a.supplierName.localeCompare(b.supplierName))
            .map((supplier) => ({
                ...supplier,
                products: supplier.products.sort((a, b) => a.productName.localeCompare(b.productName)),
            }));
    }, [menuProductArrangements, productArrangementsToUpdate]);

    const totalProducts = useMemo(() => {
        return supplierSummaries.reduce((sum, supplier) => sum + supplier.products.length, 0);
    }, [supplierSummaries]);

    const totalQuantity = useMemo(() => {
        return supplierSummaries.reduce(
            (sum, supplier) => sum + supplier.products.reduce((pSum, p) => pSum + p.totalQuantity, 0),
            0,
        );
    }, [supplierSummaries]);

    if (supplierSummaries.length === 0) {
        return (
            <Container>
                <Title>Résumé des produits par fournisseur</Title>
                <EmptyMessage>Aucun produit à afficher</EmptyMessage>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <Title>Résumé des produits par fournisseur</Title>
                <Stats>
                    <Stat>
                        <StatLabel>Fournisseurs:</StatLabel>
                        <StatValue>{supplierSummaries.length}</StatValue>
                    </Stat>
                    <Stat>
                        <StatLabel>Produits uniques:</StatLabel>
                        <StatValue>{totalProducts}</StatValue>
                    </Stat>
                    <Stat>
                        <StatLabel>Quantité totale:</StatLabel>
                        <StatValue>{totalQuantity}</StatValue>
                    </Stat>
                </Stats>
            </Header>

            <SuppliersGrid>
                {supplierSummaries.map((supplier) => (
                    <SupplierCard key={supplier.supplierId} supplier={supplier} />
                ))}
            </SuppliersGrid>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
    border-top: 1px solid ${({ theme }) => theme.darkBorderColor};
    margin: 20px;
    padding-top: 30px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 2px solid ${({ theme }) => theme.darkBorderColor};
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: bold;
`;

const Stats = styled.div`
    display: flex;
    gap: 30px;
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const StatLabel = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.textColor}99;
    margin-bottom: 4px;
`;

const StatValue = styled.span`
    font-size: 20px;
    font-weight: bold;
    color: ${({ theme }) => theme.ctaPrimaryColor || '#00b54b'};
`;

const SuppliersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    align-items: start;
`;

const EmptyMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: ${({ theme }) => theme.textColor}99;
    font-size: 16px;
`;
